"use client";

import { useEffect, useRef, useState } from "react";
import type { CapabilitySnapshot } from "@/features/ats-sync/types";
import type { PipelineActionBody } from "@/features/jobs/store/pipeline/selection";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalClose,
} from "@/components/ui/modal";
import { Button, SubmitButton } from "@/components/ui/button";
import { showToast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Icon } from "@/components/ui/icon";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { rejectAvailability } from "../lib/gating";
import { useRejectionReasons } from "../hooks/use-rejection-reasons";
import { useRejectInAts } from "../hooks/use-reject-in-ats";
import { useRejectInAtsBulk } from "../hooks/use-reject-in-ats-bulk";

interface CommonRejectInAtsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
  snapshot: CapabilitySnapshot;
  ats: string;
  onRejected: () => void;
}

type RejectInAtsModalProps = CommonRejectInAtsModalProps &
  (
    | {
        candidateIds: string[];
        selection?: never;
        jobId?: never;
        selectionCount?: never;
        onRejectUnavailable?: never;
      }
    | {
        candidateIds?: never;
        selection: PipelineActionBody;
        jobId: string;
        selectionCount: number;
        onRejectUnavailable?: () => Promise<void> | void;
      }
  );

export function RejectInAtsModal(props: RejectInAtsModalProps) {
  const { open, onOpenChange, connectionId, snapshot, ats, onRejected } = props;
  const { reasons, hasReasons, isLoading } = useRejectionReasons(
    connectionId,
    open,
  );
  const { reject, isPending } = useRejectInAts(connectionId);
  const { rejectBulk, isPending: isBulkPending } = useRejectInAtsBulk(connectionId);
  const [reasonId, setReasonId] = useState("");
  const [note, setNote] = useState("");
  const fallbackStarted = useRef(false);

  const descriptor =
    props.selection === undefined
      ? null
      : {
          selection: props.selection,
          jobId: props.jobId,
          selectionCount: props.selectionCount,
          onRejectUnavailable: props.onRejectUnavailable,
        };
  const isDescriptor = descriptor !== null;
  const candidateIds = props.candidateIds ?? [];
  const selectionCount = descriptor?.selectionCount ?? candidateIds.length;
  const confirmLabel =
    selectionCount > 1 ? `Reject ${selectionCount} in ${ats}` : `Reject in ${ats}`;

  const availability = rejectAvailability(snapshot, ats, hasReasons);
  // While reasons are still loading, hasReasons is transiently false, which
  // would otherwise flash the "no rejection reasons" dead-end on a cold open.
  // Suppress the block until the query settles (review-2 finding #10).
  const showBlock = !isLoading && !availability.allowed;
  const showForm = !isLoading && availability.allowed;

  useEffect(() => {
    if (!open) fallbackStarted.current = false;
  }, [open]);

  useEffect(() => {
    if (
      !showBlock ||
      !descriptor?.onRejectUnavailable ||
      fallbackStarted.current
    ) {
      return;
    }
    fallbackStarted.current = true;
    void Promise.resolve(descriptor.onRejectUnavailable())
      .then(() => onOpenChange(false))
      .catch(() => {
        fallbackStarted.current = false;
      });
  }, [descriptor, onOpenChange, showBlock]);

  const mayEmail = ["yes", "config_dependent"].includes(
    snapshot.features?.reject_application?.attributes?.sends_candidate_email ?? "",
  );

  const handleConfirm = async () => {
    if (!reasonId) return;
    try {
      if (descriptor) {
        const res = await rejectBulk({
          jobId: descriptor.jobId,
          selection: descriptor.selection,
          rejectionReasonId: reasonId,
          note,
        });
        const rejected = res.rejected ?? 0;
        const archivedLocal = res.archived_local ?? 0;
        if (rejected > 0 && archivedLocal === 0) {
          showToast.success(`Rejected ${rejected} in ${ats}`);
        } else if (rejected === 0 && archivedLocal > 0) {
          showToast.success(`Archived ${archivedLocal} profiles`);
        } else if (rejected > 0 && archivedLocal > 0) {
          showToast.success(
            `Rejected ${rejected} in ${ats}; archived ${archivedLocal} locally`,
          );
        }
        if (res.truncated) {
          showToast.warning(
            `Only the first ${res.resolved_count ?? res.results.length} profiles were processed.`,
          );
        }
      } else {
        await reject({
          candidateId: candidateIds[0],
          rejectionReasonId: reasonId,
          note,
        });
        showToast.success("Rejected 1 profile in " + ats);
      }
    } catch {
      // useRejectInAts/useRejectInAtsBulk already surfaced the failure via
      // toast. Keep the modal open and do NOT call onRejected so a failed
      // reject isn't treated as done.
      return;
    }
    onRejected();
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="md">
        <ModalHeader title={confirmLabel} iconName="IconUserBlock" />
        <ModalBody>
          {isLoading ? (
            <p className="text-sm text-contrast/70 leading-normal">
              Checking {ats} rejection options…
            </p>
          ) : showBlock ? (
            <p className="text-sm text-contrast/70 leading-normal">
              {availability.reason}
            </p>
          ) : (
            <div className="flex flex-col gap-5">
              {mayEmail && (
                <div className="flex items-start gap-2.5 rounded-lg border border-[#FFAF53]/25 bg-[#FFAF53]/[0.06] px-3.5 py-2.5">
                  <Icon
                    name="IconExclamationTriangle"
                    size={15}
                    className="mt-0.5 shrink-0 text-[#FFAF53]"
                  />
                  <p className="text-[13px] leading-relaxed text-contrast/70">
                    Rejecting may email the candidate, depending on {ats}&apos;s
                    configuration.
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <div className="flex items-baseline justify-between">
                  <Label>Rejection reason</Label>
                  <span className="text-xs text-contrast/40">Required</span>
                </div>
                <RadioGroup
                  value={reasonId}
                  onValueChange={setReasonId}
                  className="max-h-[264px] gap-1.5 overflow-y-auto pr-0.5"
                >
                  {reasons.map((r) => {
                    const selected = reasonId === r.id;
                    return (
                      // Whole row is the label, so a click anywhere — text or
                      // circle — selects the reason (not just the radio dot).
                      <label
                        key={r.id}
                        onClick={() => setReasonId(r.id)}
                        className={cn(
                          "flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors",
                          selected
                            ? "border-primary/50 bg-primary/[0.06]"
                            : "border-border/60 hover:border-border hover:bg-contrast/[0.03]",
                        )}
                      >
                        <RadioGroupItem value={r.id} />
                        <span
                          className={cn(
                            "text-sm text-heading",
                            selected && "font-medium",
                          )}
                        >
                          {r.name}
                        </span>
                      </label>
                    );
                  })}
                </RadioGroup>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label>Note (optional)</Label>
                <Input
                  variant="modal"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add an optional note…"
                />
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <ModalClose asChild>
            <Button variant="secondary" size="sm">
              Cancel
            </Button>
          </ModalClose>
          {showForm && (
            <SubmitButton
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleConfirm}
              disabled={!reasonId || (isDescriptor ? isBulkPending : isPending)}
              loading={isDescriptor ? isBulkPending : isPending}
              loadingLabel="Rejecting"
            >
              {confirmLabel}
            </SubmitButton>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
